/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import {
  Card, CardTitle, CardBody, CardText, Container, Row, Col,
} from 'reactstrap';
import { useQuery } from '@apollo/react-hooks';
import semver from 'semver';
import moment from 'moment';
import { GET_BOX_DATA, GET_CHANNELS } from '../../query/query';
import Admin from '../sav-lovebox/infos/admin';
import ToggleColorFeature from '../sav-lovebox/infos/toggle-color-feature';
import { formatDate, getBoxType, getLanguage } from '../../services/format-utils';
import Sender from '../sav-lovebox/infos/sender';
import DeleteSenders from '../sav-lovebox/infos/delete-senders';
import RefreshPairingCode from '../sav-lovebox/infos/refresh-pairing-code';
import { LoveboxTable } from '../sav-lovebox/table/lovebox-table';
import ToggleCrazyHeartFirmware from '../sav-lovebox/infos/toggle-crazy-heart-firmware';

const amplitudeURL = 'https://analytics.amplitude.com/loveboxlove/activity';

const GeneralData = ({
  boxId, data, hardware, isLovepix, refetchBox, versions: { lovepixLastVersion } = {},
}) => {
  const [displayLastVersion] = useState(isLovepix && (data.versions || {}).currentVersion && lovepixLastVersion && hardware !== 'C1');

  return (
    <CardText>
      <b>Name:</b>
      {' '}
      {data.name}
      <br />
      <b>MAC Address:</b>
      {' '}
      {isLovepix ? data.macAddress : data.macAddr}
      <br />
      <b>Version:</b>
      {' '}
      {(data.versions || {}).currentVersion || data.version}
      {' '}
      {displayLastVersion && (
        <span>
          {semver.compare(data.versions.currentVersion, lovepixLastVersion) >= 0 ? '✅' : '❌'}
          {' '}
          (last version:
          {' '}
          {lovepixLastVersion}
          )
        </span>
      )}
      <br />
      {
        (data.versions || {}) && (
          <span>
            <b>Firmware Group:</b>
            {' '}
            {data.versions.testGroup ? data.versions.testGroup : 'production'}
            <br />
          </span>
        )
      }
      <b>Type:</b>
      {' '}
      {getBoxType(data)}
      <br />
      <b>Language:</b>
      {' '}
      {getLanguage(data.language)}
      <br />
      <b>Secret code:</b>
      {' '}
      {data.pairing}
      <br />
      <b>Is connected:</b>
      {' '}
      {data.isConnected ? '✅' : '❌'}
      <br />
      {
        data.lastConnection
          ? (
            <span>
              <b>Last connection:</b>
              {' '}
              {moment(data.lastConnection).format('dddd, MMMM Do YYYY, h:mm:ss a')}
            </span>
          ) : <br />
      }
      <br />
      <Admin admin={data.admin} senders={data.senders} boxId={boxId} />
      {data.hardware && data.hardware.includes('C') && (
        <div>
          <span>
            <br />
            <br />
            <ToggleColorFeature boxId={boxId} hasColor={data.hasColor} refetchBox={refetchBox} />
          </span>
          <span>
            <br />
            <br />
            <ToggleCrazyHeartFirmware
              boxId={boxId}
              hasCrazyHeartFirmware={data.versions
                && data.versions.targetVersion === '4.0.54'}
              refetchBox={refetchBox}
            />
          </span>
        </div>
      )}
      <span>
        <b>Amplitude Box Lookup: </b>
        <a href={amplitudeURL}>URL</a>
        {' '}
        with box admin ID:
        {' '}
        <b>{data.adminId}</b>
      </span>
    </CardText>
  );
};

const SenderList = ({ senders, boxId }) => (
  <div>
    <b>senders:</b>
    <ul>
      {senders.map((sender) => (
        <Sender
          key={sender}
          sender={sender}
          boxId={boxId}
        />
      ))}
    </ul>
  </div>
);

const LastRequestsTime = ({
  lastRequests: {
    config,
    getState,
    ota,
    token,
  },
} = {}) => (
  <div>
    <b>Last requests:</b>
    {' '}
    (timezone = GMT)
    <ul>
      {getState
        ? (
          <li>
            <b>/get-state (update every hour at least):</b>
            {' '}
            {formatDate(getState)}
          </li>
        )
        : null}
      {config
        ? (
          <li>
            <b>/config:</b>
            {' '}
            {formatDate(config)}
          </li>
        )
        : null}
      {token
        ? (
          <li>
            <b>/regenerate-access-token:</b>
            {' '}
            {formatDate(token)}
          </li>
        )
        : null}
      {ota
        ? (
          <li>
            <b>/update:</b>
            {' '}
            {formatDate(ota)}
          </li>
        )
        : null}
    </ul>
  </div>
);
const ChannelsData = ({ channelsIds, channels }) => (
  <div>
    <b>Subscribed channels:</b>
    <ul>
      {channelsIds.map((channelId) => {
        const channel = channels.find(({ _id }) => _id === channelId);
        if (!channel) return null;
        return <li><b>{`${channel.nameEn} (${channel.nameFr})`}</b></li>;
      })}
    </ul>
  </div>
);

const SAVGetBoxData = ({
  boxId: boxIdProps, macAddress, pairing, versions,
}) => {
  const [activeTab, setTabChange] = useState('1');
  const {
    data: {
      savGetBoxData = {},
    } = {},
    error,
    loading,
    refetch,
  } = useQuery(GET_BOX_DATA, {
    fetchPolicy: 'no-cache',
    variables: { boxId: boxIdProps, macAddress, pairing },
  });

  const {
    _id: boxId,
    hardware,
    lastRequests,
    senders = [],
  } = savGetBoxData || {};

  console.log('boxIdProps', boxIdProps);
  console.log('boxId', boxId);
  console.log('savGetBoxData', savGetBoxData);

  const {
    data: { savGetChannels = [] } = {},
    loading: isLoadingChannels,
    error: errorChannels,
    refetch: refetchChannels,
  } = useQuery(GET_CHANNELS, {
    fetchPolicy: 'no-cache',
    variables: { boxId: boxId || boxIdProps },
  });
  
  const refetchBox = async () => {
    await refetch({ variables: { boxId: boxIdProps, macAddress, pairing } });
    await refetchChannels({ variables: { boxId: boxId || boxIdProps } });
  };
  if (loading || isLoadingChannels) return <p>Loading...</p>;
  if (error || errorChannels) {
    console.log('error', error);
    console.log('errorChannels', errorChannels);
    return <p>Error</p>;
  }
  if (!boxId) return <p>No box found</p>;

  const isLovepix = hardware && hardware[0] === 'C';
  return (
    <div>
      <Container>
        <Row>
          <Col>
            <GeneralData
              boxId={boxId}
              data={savGetBoxData}
              hardware={hardware}
              isLovepix={isLovepix}
              refetchBox={refetchBox}
              versions={versions}
            />
          </Col>
          <Col>
            <SenderList senders={senders} boxId={boxId} />
          </Col>
        </Row>
      </Container>
      <br />
      <br />
      {(isLovepix && lastRequests) && (
        <Container>
          <LastRequestsTime lastRequests={lastRequests} />
        </Container>
      )}
      <br />
      <Container>
        <ChannelsData
          channelsIds={savGetBoxData.channelsIds ? savGetBoxData.channelsIds : []}
          channels={savGetChannels}
        />
      </Container>
      <br />
      <br />
      <br />
      <Container>
        <Row>
          <Col>
            <DeleteSenders
              boxId={boxId}
            />
          </Col>
          <Col>
            <RefreshPairingCode
              boxId={boxId}
              isLovepix={isLovepix}
            />
          </Col>
        </Row>
      </Container>
      <Container>
        <LoveboxTable
          selectedLovebox={boxId}
          activeTab={activeTab}
          onTabChange={setTabChange}
        />
      </Container>
    </div>
  );
};

export default ({
  boxId, macAddress, versions, pairing,
}) => (
  <Card>
    <CardBody>
      <CardTitle>
        Lovebox data
      </CardTitle>
      <SAVGetBoxData
        boxId={boxId}
        macAddress={macAddress}
        pairing={pairing}
        versions={versions}
      />
    </CardBody>
  </Card>
);



// WEBPACK FOOTER //
// ./src/components/lovebox-info/lovebox-info.js